import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoForPath, SITE_ORIGIN } from '../config/seo.ts';

/**
 * Per-route head manager. On every navigation, rewrites the document title,
 * meta description, canonical URL, and the Open Graph / Twitter mirrors to the
 * route's entry in src/config/seo.ts (falling back to the site default).
 *
 * The tags themselves ship statically in index.html with the home-page values
 * — crawlers and scrapers that don't execute JS still get correct metadata for
 * `/`; this component retargets them for client-side navigations and deep
 * loads of other routes. Tags are created if absent so unit tests (jsdom has
 * an empty head) and future head changes can't silently no-op.
 */
export default function SeoHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    const { title, description } = seoForPath(pathname);
    const canonical = `${SITE_ORIGIN}${pathname === '/' ? '/' : pathname}`;

    document.title = title;
    setTag('meta[name="description"]', 'content', description, ['name', 'description']);
    setTag('link[rel="canonical"]', 'href', canonical, ['rel', 'canonical']);
    setTag('meta[property="og:title"]', 'content', title, ['property', 'og:title']);
    setTag('meta[property="og:description"]', 'content', description, ['property', 'og:description']);
    setTag('meta[property="og:url"]', 'content', canonical, ['property', 'og:url']);
    setTag('meta[name="twitter:title"]', 'content', title, ['name', 'twitter:title']);
    setTag('meta[name="twitter:description"]', 'content', description, ['name', 'twitter:description']);
  }, [pathname]);
  return null;
}

function setTag(
  selector: string,
  attr: string,
  value: string,
  [keyAttr, keyValue]: [string, string],
): void {
  let el = document.head.querySelector(selector);
  if (el === null) {
    el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
    el.setAttribute(keyAttr, keyValue);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}
